import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import roleConfig from "../../common/layout/roleConfig";
import dataService from "../../../helpers/dataService";
import { mapSynchroFTToEvent, mergeAndSort } from "./synchroFTTimelineMapper";
import { mapDemandeSuspensionRemobilisationToEvents } from "./suspensionRemobilisationTimelineMapper";

// Données et actions de la fiche allocataire — logique extraite à l'identique
// de l'ancien composant RecipientDetail (mêmes routes, mêmes messages, mêmes
// filtres à l'enregistrement), la page ne fait plus que composer.

export const EMPTY_RECIPIENT = {
  email: "",
  nir: "",
  gender: "",
  firstname: "",
  lastname: "",
  birthdate: "",
  cityId: "",
  address: "",
  addressAdd: "",
  location: { cityId: "", postalCode: "", name: "", locationName: "" },
  phone: "",
  phone2: "",
  maritalStatus: "",
  nbChildrenOrDependant: "",
  CAFId: "",
  rights: [],
  origin: "",
  rightsAndDuties: [],
  contracts: [],
  professionalProject: "",
  driveLicenses: [],
  transports: [],
  numSubscribePE: "",
  userManager: "",
  jobSearch: "",
  jobSearchExp: "",
  job2Search: "",
  job2SearchExp: "",
};

// Corps envoyé à l'API : les lignes vides des listes dynamiques sont retirées.
export function buildRecipientSavePayload(recipient) {
  return {
    ...recipient,
    rights: (recipient.rights || []).filter((r) => r.type),
    rightsAndDuties: (recipient.rightsAndDuties || []).filter((r) => r.beginAt),
    contracts: (recipient.contracts || []).filter((c) => c.type),
  };
}

// Applique un changement de champ (Control, DynamicList, checkBtnList) à
// l'allocataire — même sémantique que l'ancien updateData.
export function applyRecipientChange(recipient, target) {
  const { type, name, value, checked, formArray } = target;
  const next = JSON.parse(JSON.stringify(recipient));
  let elem;
  if (type === "checkbox") {
    elem = !!checked;
  } else if (formArray) {
    elem = next[name] || [];
    if (type === "checkBtnList") {
      elem = elem.includes(value) ? elem.filter((v) => v !== value) : [...elem, value];
    } else if (formArray.target && formArray.target.checked) {
      elem = [...elem, value];
    } else {
      elem = elem.filter((v) => v !== value);
    }
  } else {
    elem = value;
  }
  next[name] = elem;
  return next;
}

export default function useRecipientDetail({ idToGet, user }) {
  const [recipient, setRecipient] = useState(EMPTY_RECIPIENT);
  const [events, setEvents] = useState(false);
  const [rdsps, setRdsps] = useState(false);
  const [rdvs, setRdvs] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(!!idToGet && idToGet !== "create");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(true);
  const [formConfig, setFormConfig] = useState(false);
  const [isLoadingRdvs, setIsLoadingRdvs] = useState(false);
  const [hasRdv, setHasRdv] = useState(false);
  const [rdspId, setRdspId] = useState(false);
  const [infoUpdated, setInfoUpdated] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [synchrosFT, setSynchrosFT] = useState([]);
  const [demandesSuspensionRemobilisation, setDemandesSuspensionRemobilisation] = useState([]);

  const role = user ? user.role : null;
  // Champs désactivés quand le rôle est privé de « update_recipients ».
  const allDisabled = !!(role && (roleConfig[role] || []).includes("update_recipients"));

  const mergedEvents = useMemo(() => {
    if (!events) return events;
    return mergeAndSort(events, [
      ...synchrosFT.map(mapSynchroFTToEvent),
      ...demandesSuspensionRemobilisation.flatMap(mapDemandeSuspensionRemobilisationToEvents),
    ]);
  }, [events, synchrosFT, demandesSuspensionRemobilisation]);

  useEffect(() => {
    if (idToGet) getDatas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToGet]);

  // Les demandes de Suspension / Remobilisation rejoignent le parcours comme les
  // synchros FT : lues à part, fusionnées à l'affichage.
  function refreshDemandesSuspensionRemobilisation() {
    dataService.get(
      `sanctions/suspension-remobilisation/recipients/${idToGet}`,
      (data) => setDemandesSuspensionRemobilisation(data ?? []),
      (err) => {
        console.warn("Demandes de Suspension / Remobilisation indisponibles", err);
        setDemandesSuspensionRemobilisation([]);
      }
    );
  }

  function refreshSynchrosFT() {
    dataService.get(
      `france-travail/allocataires/${idToGet}/synchronisations`,
      (data) => setSynchrosFT(data ?? []),
      (err) => {
        console.warn("Synchros FT indisponibles", err);
        setSynchrosFT([]);
      }
    );
  }

  function refreshRdsps() {
    dataService.get(`recipients/${idToGet}/rdsp`, (data) => setRdsps(data.reverse()), setErrors);
  }

  function getRdvs() {
    setIsLoadingRdvs(true);
    dataService.get(`rdv/${idToGet}`, setRdvs, setErrors, () => {
      setIsLoading(false);
      setIsLoadingRdvs(false);
    });
  }

  function getDatas() {
    dataService.get("organizations/orientation_partners", setOrgs);
    if (idToGet !== "create") {
      setIsLoading(true);
      dataService.get(
        `recipients/${idToGet}`,
        (data) => setRecipient({ ...data, cityId: data.location?.cityId }),
        setErrors,
        () => setIsLoading(false)
      );
    }
    dataService.get(`rdsp`, setFormConfig, setErrors);
    refreshEvents();
    refreshRdsps();
    dataService.get(
      `rdsp/recipients/${idToGet}/check`,
      (data) => {
        const { rdsp, rdv } = data;
        if (!rdsp) {
          toast.info("Aucun RDO rempli par cet allocataire");
        } else {
          setRdspId(rdsp);
        }
        if (rdv) setHasRdv(true);
      },
      () => toast.error("Une erreur s'est produite pendant la récupération des informations")
    );
    if (role !== "AGENT_PARTNER") getRdvs();
    refreshSynchrosFT();
    refreshDemandesSuspensionRemobilisation();
  }

  // La timeline seule, après une action qui y ajoute un évènement (correction de la sous-orientation, CE-1370).
  function refreshEvents() {
    dataService.get(`recipients/${idToGet}/timeline`, setEvents, setErrors);
  }

  function updateData(e) {
    // On photographie la cible tout de suite : un input contrôlé peut être
    // re-rendu (et sa valeur DOM réinitialisée) avant l'exécution de l'updater.
    const { type, name, value, checked, formArray } = e.target;
    const target = { type, name, value, checked, formArray };
    setIsSaved(false);
    setInfoUpdated(true);
    setRecipient((current) => applyRecipientChange(current, target));
  }

  function resendActivationMail() {
    setIsLoading(true);
    dataService.get(
      `recipients/${recipient.id}/renew-activation`,
      () => toast.success(`L'email d'activation a bien été renvoyé à ${recipient.email}`),
      (err) => {
        toast.error("Une erreur s'est produite pendant l'envoi de l'email d'activation");
        setErrors(err);
      },
      () => setIsLoading(false)
    );
  }

  function save() {
    setIsSaving(true);
    setIsSaved(false);
    setErrors({});
    const recipientDatas = buildRecipientSavePayload(recipient);
    const done = () => {
      setIsSaving(false);
      setInfoUpdated(false);
    };
    if (idToGet === "create") {
      dataService.post(
        `recipients`,
        recipientDatas,
        (data) => {
          setRecipient({ ...data, cityId: data.location?.cityId });
          setIsSaving(false);
          setIsSaved(true);
          toast.success("Création de l'allocataire réussie");
          window.location.href = "/recipients/" + data.id;
        },
        setErrors,
        done
      );
    } else {
      dataService.patch(
        `recipients/${idToGet}`,
        recipientDatas,
        (data) => {
          setRecipient({ ...data, cityId: data.location?.cityId });
          setIsSaving(false);
          setIsSaved(true);
          toast.success("Modification de l'allocataire réussie");
        },
        setErrors,
        done
      );
    }
  }

  function unlockAccount() {
    dataService.get(
      `recipients/${idToGet}/authorization/reset`,
      () => toast.success("Compte débloqué avec succès."),
      (err) => {
        toast.error("Une erreur est survenue lors du déblocage de ce compte allocataire.");
        setErrors(err);
      }
    );
  }

  return {
    recipient,
    events,
    mergedEvents,
    rdsps,
    rdvs,
    errors,
    setErrors,
    isLoading,
    isSaving,
    isSaved,
    allDisabled,
    formConfig,
    isLoadingRdvs,
    hasRdv,
    rdspId,
    infoUpdated,
    orgs,
    getDatas,
    getRdvs,
    refreshEvents,
    refreshRdsps,
    refreshSynchrosFT,
    refreshDemandesSuspensionRemobilisation,
    demandesSuspensionRemobilisation,
    updateData,
    save,
    resendActivationMail,
    unlockAccount,
  };
}
